import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { startProductTour } from "@/lib/tour";
import SchoolSettingsModal from "@/components/SchoolSettingsModal";
import AccountSecurityModal from "@/components/AccountSecurityModal";
import HelpGuide from "@/components/HelpGuide";
import LayoutHeader from "@/components/LayoutHeader";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [securityOpen, setSecurityOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpEngaged, setHelpEngaged] = useState(() => !!localStorage.getItem("bloomtrack_help_engaged"));

  const openHelp = () => {
    setHelpOpen(true);
    if (!localStorage.getItem("bloomtrack_help_engaged")) {
      localStorage.setItem("bloomtrack_help_engaged", "1");
      setHelpEngaged(true);
    }
  };

  // Auto-open the guide once on first visit, and let any screen open it via an event.
  useEffect(() => {
    if (!localStorage.getItem("bloomtrack_help_seen")) {
      setHelpOpen(true);
      localStorage.setItem("bloomtrack_help_seen", "1");
    }
    const onOpen = () => openHelp();
    window.addEventListener("bloomtrack:open-help", onOpen);
    return () => window.removeEventListener("bloomtrack:open-help", onOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep-link a guide card to the most relevant screen so teachers can try it now.
  const handleHelpNav = async (key) => {
    setHelpOpen(false);
    if (key === "meetings") return navigate("/meetings");
    if (key === "planning") return navigate("/groups");
    if (key === "settings") return setSettingsOpen(true);
    if (key === "classes") return navigate("/");
    let cid = null;
    try { const r = await api.get("/classes"); cid = r.data?.[0]?.id; }
    catch (err) { console.warn("Help nav: could not load classes", err); }
    if (key === "cos") return navigate(cid ? `/class/${cid}/continuum` : "/");
    if (key === "overview") return navigate(cid ? `/class/${cid}/map` : "/");
    return navigate(cid ? `/class/${cid}` : "/");
  };

  const handleStartTour = () => {
    setHelpOpen(false);
    navigate("/");
    setTimeout(() => startProductTour(), 450);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <LayoutHeader
        user={user}
        meetingsActive={location.pathname.startsWith("/meetings")}
        onHome={() => navigate("/")}
        onMeetings={() => navigate("/meetings")}
        onHelp={openHelp}
        helpEngaged={helpEngaged}
        onSecurity={() => setSecurityOpen(true)}
        onSettings={() => setSettingsOpen(true)}
        onLogout={handleLogout}
      />
      {settingsOpen && <SchoolSettingsModal onClose={() => setSettingsOpen(false)} />}
      {securityOpen && <AccountSecurityModal onClose={() => setSecurityOpen(false)} />}
      {helpOpen && <HelpGuide onClose={() => setHelpOpen(false)} onNavigate={handleHelpNav} onStartTour={handleStartTour} />}
      <main
        key={location.pathname}
        className="max-w-6xl mx-auto px-5 md:px-8 py-8 md:py-10 animate-rise"
      >
        {children}
      </main>
    </div>
  );
}
